(() => {
  const slug = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const colorForMaterial = (name) => {
    const value = String(name || '').toLowerCase();
    const palette = [
      [/rust|copper|burnt/, '#92532f'],
      [/london|tan|camel|buckskin/, '#c59a62'],
      [/pink|rose/, '#d8949f'],
      [/black|onyx/, '#1d1d1b'],
      [/dark brown|chocolate/, '#543726'],
      [/brown|leather/, '#825235'],
      [/grey|gray|slate/, '#777874'],
      [/navy|blue/, '#30485e'],
      [/green|olive/, '#5f6843'],
      [/red|burgundy/, '#8d2f34'],
      [/white|cream|ivory/, '#eee9dd']
    ];
    const match = palette.find(([pattern]) => pattern.test(value));
    if (match) return match[1];
    if (/other|confirm|custom/.test(value)) return '#b6b6b2';
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) hash = ((hash << 5) - hash) + value.charCodeAt(index);
    return `hsl(${Math.abs(hash) % 360} 22% 48%)`;
  };

  const defaultVolumeTiers = [
    { min: 1, max: 11, rate: 0, label: '1–11', savings: '—' },
    { min: 12, max: 23, rate: .10, label: '12–23', savings: '10%' },
    { min: 24, max: 35, rate: .15, label: '24–35', savings: '15%' },
    { min: 36, max: 49, rate: .20, label: '36–49', savings: '20%' },
    { min: 50, max: 100, rate: .25, label: '50–100', savings: '25%' },
    { min: 101, max: 199, rate: .30, label: '101–199', savings: '30%' },
    { min: 200, max: Infinity, rate: .35, label: '200+', savings: '35%' }
  ];

  const parseVolumeTiers = (source) => {
    const rows = String(source || '').split(/\r?\n/).map((row) => row.trim()).filter(Boolean);
    const parsed = rows.map((row) => {
      const [minimum, discount] = row.split('|').map((value) => Number(String(value || '').trim()));
      return { min: minimum, rate: discount / 100 };
    }).filter((tier) => Number.isFinite(tier.min) && tier.min > 0 && Number.isFinite(tier.rate) && tier.rate >= 0 && tier.rate < 1)
      .sort((a, b) => a.min - b.min);
    if (!parsed.length || parsed[0].min !== 1) return defaultVolumeTiers;
    return parsed.map((tier, index) => {
      const next = parsed[index + 1];
      const maximum = next ? next.min - 1 : Infinity;
      const percent = Math.round(tier.rate * 100);
      return {
        ...tier,
        max: maximum,
        label: maximum === Infinity ? `${tier.min}+` : `${tier.min}–${maximum}`,
        savings: percent > 0 ? `${percent}%` : '—'
      };
    });
  };

  const tierForQuantity = (tiers, quantity) => [...tiers].reverse().find((tier) => quantity >= tier.min) || tiers[0];

  const artDirectHero = () => {
    document.querySelectorAll('.wb-hero__slide--headwear .wb-hero__heading').forEach((heading) => {
      if (heading.dataset.wbArtDirected === 'true') return;
      const normalized = heading.textContent.replace(/\s+/g, ' ').trim().toUpperCase();
      if (normalized !== 'CUSTOM HEADWEAR. BUILT YOUR WAY.') return;
      const lines = ['CUSTOM', 'HEADWEAR.', 'BUILT YOUR', 'WAY.'];
      heading.textContent = '';
      lines.forEach((line) => {
        const span = document.createElement('span');
        span.className = 'wb-hero__heading-line';
        span.textContent = line;
        heading.appendChild(span);
      });
      heading.dataset.wbArtDirected = 'true';
      heading.setAttribute('aria-label', 'Custom Headwear. Built Your Way.');
    });
  };

  const enhanceShapeButtons = (customizer) => {
    customizer.querySelectorAll('[data-wb-shape]').forEach((button) => {
      if (button.dataset.wbVisualized === 'true') return;
      const label = button.dataset.wbShape || button.textContent.trim();
      const style = button.dataset.wbShapeStyle || slug(label);
      const image = button.dataset.wbShapeImage || '';
      const icon = document.createElement('span');
      icon.className = `wb-shape-swatch wb-shape-swatch--${slug(style)}`;
      if (image) {
        icon.classList.add('wb-shape-swatch--image');
        icon.style.setProperty('--wb-shape-image', `url("${image.replace(/"/g, '\\"')}")`);
      }
      icon.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.textContent = label;
      button.textContent = '';
      button.append(icon, text);
      button.dataset.wbVisualized = 'true';
      button.setAttribute('aria-label', `${label} patch shape`);
    });
  };

  const enhanceMaterialButtons = (customizer) => {
    customizer.querySelectorAll('[data-wb-material]').forEach((button) => {
      if (button.dataset.wbVisualized === 'true') return;
      const label = button.dataset.wbMaterial || button.textContent.trim();
      const configuredColor = button.dataset.wbMaterialColor || colorForMaterial(label);
      const image = button.dataset.wbMaterialImage || '';
      const swatch = document.createElement('span');
      swatch.className = 'wb-material-swatch';
      swatch.setAttribute('aria-hidden', 'true');
      swatch.style.setProperty('--wb-material-color', configuredColor);
      if (image) swatch.style.setProperty('--wb-material-image', `url("${image.replace(/"/g, '\\"')}")`);
      const text = document.createElement('span');
      text.textContent = label;
      button.textContent = '';
      button.append(swatch, text);
      button.dataset.wbVisualized = 'true';
      button.setAttribute('aria-label', `${label} patch material`);
    });
  };

  const enhanceEmbroideryChoice = (customizer) => {
    const button = [...customizer.querySelectorAll('[data-wb-decoration]')]
      .find((choice) => /direct embroidery/i.test(choice.dataset.wbDecoration || ''));
    if (!button || button.dataset.wbToggleEnhanced === 'true') return;
    const price = Number(button.dataset.wbAddonPrice || 4000);
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: customizer.dataset.currency || 'USD'
    }).format(price / 100);
    const box = document.createElement('span');
    box.className = 'wb-embroidery-toggle__box';
    box.setAttribute('aria-hidden', 'true');
    box.innerHTML = '<svg viewBox="0 0 20 20" fill="none"><path d="m4 10 4 4 8-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const content = document.createElement('span');
    content.className = 'wb-embroidery-toggle__content';
    const title = document.createElement('strong');
    title.textContent = 'Add Direct Embroidery';
    const help = document.createElement('small');
    help.textContent = 'Add direct embroidery to this custom order.';
    const fee = document.createElement('em');
    fee.textContent = `One-time digitizing fee: ${formatted} per logo/design — never per hat.`;
    content.append(title, help, fee);
    button.textContent = '';
    button.append(box, content);
    button.classList.add('wb-choice--embroidery-toggle');
    button.dataset.wbToggleEnhanced = 'true';
    button.setAttribute('role', 'checkbox');
    const sync = () => button.setAttribute('aria-checked', button.classList.contains('is-selected') ? 'true' : 'false');
    new MutationObserver(sync).observe(button, { attributes: true, attributeFilter: ['class'] });
    sync();
  };

  const init = (customizer) => {
    if (!customizer || customizer.dataset.wbEnhanced === 'true') return;
    customizer.dataset.wbEnhanced = 'true';

    const form = document.getElementById(customizer.dataset.formId);
    const productRoot = customizer.closest('[data-wb-product-root]');
    const previewBase = productRoot?.querySelector('[data-wb-preview-base]');
    const safeZone = productRoot?.querySelector('[data-wb-safe-zone]');
    const placements = [...customizer.querySelectorAll('[data-wb-placement]')];
    const shapeButtons = [...customizer.querySelectorAll('[data-wb-shape]')];
    const materialButtons = [...customizer.querySelectorAll('[data-wb-material]')];
    const permission = customizer.querySelector('[data-wb-permission]');
    const embroideryWrap = customizer.querySelector('[data-wb-embroidery-wrap]');
    const embroideryAck = customizer.querySelector('[data-wb-embroidery-ack]');
    const sewWrap = customizer.querySelector('[data-wb-sew-wrap]');
    const sewCheck = customizer.querySelector('[data-wb-sew-check]');
    const addonsInput = customizer.querySelector('[data-wb-property="addons"]');
    const placementInput = customizer.querySelector('[data-wb-property="placement"]');
    const patchOptions = customizer.querySelector('[data-wb-patch-options]');
    const placementStatus = customizer.querySelector('[data-wb-placement-value]');
    const baseSrc = previewBase?.getAttribute('src') || previewBase?.dataset.baseSrc || '';
    const customizerMode = customizer.dataset.customizerMode || 'hat';
    const showVolumePricing = customizer.dataset.showVolumePricing === 'true';
    const unitNoun = customizerMode === 'tumbler' ? 'tumbler' : 'hat';
    const collectionNoun = customizerMode === 'tumbler' ? 'tumblers' : 'hats';
    const volumeTiers = showVolumePricing
      ? parseVolumeTiers(customizer.dataset.wbVolumeTiers)
      : [{ min: 1, max: Infinity, rate: 0, label: '1+', savings: '—' }];

    enhanceShapeButtons(customizer);
    enhanceMaterialButtons(customizer);

    let volumePricing = null;
    let volumeGrid = null;
    const totalBar = customizer.querySelector('.wb-builder__totalbar');
    if (showVolumePricing) {
      volumePricing = document.createElement('section');
      volumePricing.className = 'wb-volume-pricing';
      volumePricing.setAttribute('aria-labelledby', `WBVolumeTitle-${customizer.id || customizer.dataset.formId}`);
      volumePricing.innerHTML = `
        <div class="wb-volume-pricing__head">
          <div><span>VOLUME PRICING</span><h3 id="WBVolumeTitle-${customizer.id || customizer.dataset.formId}">BUY MORE &amp; SAVE</h3></div>
          <p>Volume pricing automatically applies to eligible ${collectionNoun}. Customization and one-time setup fees are calculated separately.</p>
        </div>
        <div class="wb-volume-pricing__grid" role="list"></div>
        <p class="wb-volume-pricing__message" data-wb-volume-message aria-live="polite"></p>`;
      totalBar?.insertAdjacentElement('afterend', volumePricing);
      volumeGrid = volumePricing.querySelector('.wb-volume-pricing__grid');
    }

    const selectedDecoration = () => customizer.querySelector('[data-wb-decoration].is-selected')?.dataset.wbDecoration || '';
    const selectedShapeButton = () => customizer.querySelector('[data-wb-shape].is-selected');
    const selectedMaterialButton = () => customizer.querySelector('[data-wb-material].is-selected:not([hidden])');
    const selectedShape = () => selectedShapeButton()?.dataset.wbShape || '';
    const selectedMaterial = () => selectedMaterialButton()?.dataset.wbMaterial || '';

    const syncPreviewSurface = () => {
      if (!safeZone) return;
      const decoration = selectedDecoration();
      const shape = selectedShape();
      const material = selectedMaterial();
      const shapeButton = selectedShapeButton();
      const materialButton = selectedMaterialButton();
      const shapeStyle = shapeButton?.dataset.wbShapeStyle || slug(shape);
      const shapeImage = shapeButton?.dataset.wbShapeImage || '';
      const materialColor = materialButton?.dataset.wbMaterialColor || colorForMaterial(material);
      const materialImage = materialButton?.dataset.wbMaterialImage || '';
      const isPatch = /patch/i.test(decoration);
      const isEmbroidery = /embroider/i.test(decoration);
      safeZone.classList.toggle('has-wb-patch', isPatch);
      safeZone.classList.toggle('has-wb-embroidery', isEmbroidery);
      if (shape) safeZone.dataset.wbPreviewShape = slug(shapeStyle);
      else delete safeZone.dataset.wbPreviewShape;
      safeZone.style.setProperty('--wb-preview-material', materialColor);
      if (materialImage) safeZone.style.setProperty('--wb-preview-texture', `url("${materialImage.replace(/"/g, '\\"')}")`);
      else safeZone.style.removeProperty('--wb-preview-texture');
      if (shapeStyle === 'custom' && shapeImage) {
        const mask = `url("${shapeImage.replace(/"/g, '\\"')}")`;
        safeZone.style.clipPath = 'none';
        safeZone.style.webkitMaskImage = mask;
        safeZone.style.maskImage = mask;
        safeZone.style.webkitMaskRepeat = 'no-repeat';
        safeZone.style.maskRepeat = 'no-repeat';
        safeZone.style.webkitMaskPosition = 'center';
        safeZone.style.maskPosition = 'center';
        safeZone.style.webkitMaskSize = 'contain';
        safeZone.style.maskSize = 'contain';
      } else {
        ['clip-path', '-webkit-mask-image', 'mask-image', '-webkit-mask-repeat', 'mask-repeat', '-webkit-mask-position', 'mask-position', '-webkit-mask-size', 'mask-size']
          .forEach((property) => safeZone.style.removeProperty(property));
      }
      const label = safeZone.querySelector('.wb-preview__safe-label');
      if (label) label.textContent = isPatch ? `${shape || 'Patch'} • ${material || 'Choose material'}` : 'DECORATION AREA';
    };

    const setPressed = (buttons, selected) => buttons.forEach((button) => {
      const active = button === selected;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const setView = (placement) => {
      if (!previewBase) return;
      const key = placement.toLowerCase().replace(/[^a-z]/g, '');
      const source = customizer.dataset[`wb${key.charAt(0).toUpperCase()}${key.slice(1)}Src`] || '';
      if (source) previewBase.src = source;
      else if (baseSrc) previewBase.src = baseSrc;
    };

    const syncPlacement = (button) => {
      const value = button?.dataset.wbPlacement || '';
      setPressed(placements, button);
      if (placementInput) placementInput.value = value;
      if (placementStatus) placementStatus.textContent = value;
      setView(value);
    };

    const syncMaterialAvailability = () => {
      const decoration = selectedDecoration().trim().toLowerCase();
      let firstVisible = null;
      materialButtons.forEach((button) => {
        const rules = String(button.dataset.wbAppliesTo || '').split(/[,|\n]/).map((value) => value.trim().toLowerCase()).filter(Boolean);
        const visible = !rules.length || rules.includes(decoration);
        button.hidden = !visible;
        if (visible && !firstVisible) firstVisible = button;
      });
      const selected = selectedMaterialButton();
      if (!selected && firstVisible) firstVisible.click();
    };

    const syncAllAddons = () => {
      const addons = [];
      const pushAddon = (id, price, label, perItem, kind) => {
        const numericId = Number(id || 0);
        if (numericId <= 0) return;
        addons.push({
          id: numericId,
          price: Number(price || 0),
          label: String(label || 'Customization'),
          perItem: perItem !== 'false' && perItem !== false,
          kind
        });
      };

      const decorationButton = customizer.querySelector('[data-wb-decoration].is-selected');
      pushAddon(
        decorationButton?.dataset.wbAddonId,
        decorationButton?.dataset.wbAddonPrice,
        decorationButton?.dataset.wbAddonLabel || selectedDecoration(),
        decorationButton?.dataset.wbAddonPerItem,
        'decoration'
      );

      [selectedShapeButton(), selectedMaterialButton()].forEach((button) => {
        pushAddon(
          button?.dataset.wbOptionFeeId,
          button?.dataset.wbOptionFeePrice,
          button?.dataset.wbOptionFeeLabel,
          button?.dataset.wbOptionFeePerItem,
          button?.hasAttribute('data-wb-shape') ? 'shape' : 'material'
        );
      });

      if (sewCheck?.checked) {
        pushAddon(
          sewCheck.dataset.wbSewId,
          sewCheck.dataset.wbSewPrice,
          sewCheck.dataset.wbSewLabel || 'Sewn On',
          true,
          'sewn-on'
        );
      }

      if (addonsInput) addonsInput.value = JSON.stringify(addons);
      return addons;
    };

    const refreshPrice = () => {
      const quantity = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value || 1));
      const activeVariant = customizer.getCurrentVariant?.();
      const baseUnit = Number(activeVariant?.price ?? customizer.basePrice ?? 0);
      const tier = tierForQuantity(volumeTiers, quantity);
      const base = Math.round(baseUnit * quantity * (1 - tier.rate));
      const addons = syncAllAddons();
      const addonTotal = addons.reduce((sum, addon) => sum + (Number(addon.price || 0) * (addon.perItem ? quantity : 1)), 0);
      const sewTotal = addons.filter((addon) => addon.kind === 'sewn-on')
        .reduce((sum, addon) => sum + (Number(addon.price || 0) * quantity), 0);
      const total = base + addonTotal;
      const money = customizer.money ? customizer.money(total) : `$${(total / 100).toFixed(2)}`;
      customizer.querySelectorAll('[data-wb-total-price]').forEach((node) => { node.textContent = money; });
      customizer.querySelectorAll('[data-wb-base-price]').forEach((node) => { node.textContent = customizer.money ? customizer.money(base) : `$${(base / 100).toFixed(2)}`; });
      const row = customizer.querySelector('[data-wb-sew-row]');
      if (row) {
        row.hidden = sewTotal <= 0;
        row.querySelector('strong').textContent = customizer.money ? customizer.money(sewTotal) : `$${(sewTotal / 100).toFixed(2)}`;
      }

      if (volumeGrid) {
        volumeGrid.textContent = '';
        volumeTiers.forEach((item) => {
          const card = document.createElement('div');
          const active = item === tier;
          const price = Math.round(baseUnit * (1 - item.rate));
          const priceText = customizer.money ? customizer.money(price) : `$${(price / 100).toFixed(2)}`;
          card.className = `wb-volume-tier${active ? ' is-current' : ''}`;
          card.setAttribute('role', 'listitem');
          if (active) card.setAttribute('aria-current', 'true');
          card.innerHTML = `<strong>${item.label}</strong><span>${item.savings === '—' ? 'Standard price' : `${item.savings} off`}</span><b>${priceText} / ${unitNoun}</b>`;
          volumeGrid.appendChild(card);
        });
      }
      const message = volumePricing?.querySelector('[data-wb-volume-message]');
      const nextTier = volumeTiers.find((item) => item.min > quantity);
      const maximumSavings = volumeTiers[volumeTiers.length - 1]?.savings || 'the maximum discount';
      if (message) {
        message.textContent = nextTier
          ? `Add ${nextTier.min - quantity} more eligible ${nextTier.min - quantity === 1 ? unitNoun : collectionNoun} to unlock ${nextTier.savings} off the base ${unitNoun} price.`
          : `You are receiving the maximum ${maximumSavings} volume savings on the base ${unitNoun} price.`;
      }
    };

    const syncConditionalControls = () => {
      const decoration = selectedDecoration();
      const isPatch = /patch/i.test(decoration);
      const isSewablePatch = /(leather|uv).*patch|patch.*(leather|uv)/i.test(decoration);
      const isEmbroidery = /embroider/i.test(decoration);
      if (patchOptions) patchOptions.hidden = !isPatch;
      syncMaterialAvailability();
      if (sewWrap) sewWrap.hidden = !isSewablePatch;
      if (!isSewablePatch && sewCheck) sewCheck.checked = false;
      if (embroideryWrap) embroideryWrap.hidden = !isEmbroidery;
      if (embroideryAck) {
        embroideryAck.required = isEmbroidery;
        if (!isEmbroidery) embroideryAck.checked = false;
      }
      syncPreviewSurface();
      setTimeout(() => { syncAllAddons(); refreshPrice(); }, 0);
    };

    placements.forEach((button) => button.addEventListener('click', () => syncPlacement(button)));
    shapeButtons.forEach((button) => button.addEventListener('click', () => setTimeout(() => { syncPreviewSurface(); syncAllAddons(); refreshPrice(); }, 0)));
    materialButtons.forEach((button) => button.addEventListener('click', () => setTimeout(() => { syncPreviewSurface(); syncAllAddons(); refreshPrice(); }, 0)));
    customizer.querySelectorAll('[data-wb-decoration]').forEach((button) => button.addEventListener('click', () => setTimeout(syncConditionalControls, 0)));
    sewCheck?.addEventListener('change', () => { syncAllAddons(); refreshPrice(); });
    form?.querySelector('[name="quantity"]')?.addEventListener('input', () => setTimeout(refreshPrice, 0));
    form?.addEventListener('change', () => setTimeout(refreshPrice, 0));

    form?.addEventListener('submit', (event) => {
      syncConditionalControls();
      syncAllAddons();
      if (permission && !permission.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        permission.setCustomValidity('Please confirm that you have permission to use the submitted design.');
        permission.reportValidity();
        permission.focus();
        return;
      }
      permission?.setCustomValidity('');
      if (embroideryAck?.required && !embroideryAck.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        embroideryAck.setCustomValidity('Please acknowledge the one-time embroidery digitizing fee.');
        embroideryAck.reportValidity();
        embroideryAck.focus();
        return;
      }
      embroideryAck?.setCustomValidity('');
    }, true);

    if (placements[0]) syncPlacement(placements[0]);
    syncConditionalControls();
  };

  const scan = () => {
    try { artDirectHero(); } catch (error) { document.documentElement.dataset.wbHeroEnhancementError = error.message; }
    document.querySelectorAll('war-buffalo-customizer').forEach((customizer) => {
      try { init(customizer); } catch (error) { customizer.dataset.wbEnhancementError = error.message; }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  window.addEventListener('load', scan, { once: true });
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
})();
